import React from 'react'

import {
  usePRFileArtboards,
  useRootNodeAbsoluteExtent,
  PRMarinaFileArtboard,
  PRMarinaFileSection,
  PRMarinaLayerType,
  usePRFileSections,
  convertEmbindVectorToArray,
} from '@sketch-hq/sketch-web-renderer'

import { EntityOverlay } from '../../../../canvas/components/EntityOverlay'
import { isUnstyledSection } from '../../../../canvas/utils'
import { useInspectorContext } from '../../../FrameView'
import {
  useViewModeState,
  useViewModeOverlayProps,
  useViewModeGlobalEvents,
} from './viewMode.hooks'
import { useInspectModeOverlayProps } from './inspectMode.hooks'
import { Rect } from '../../../../inspector/types'
import { useCanvasAnnotations } from '../../../../annotations/hooks'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'
/**
 * The objectIds of the focused Section's direct child Sections - Sections whose
 * nearest Section ancestor is the focused Section. These are the drill-in
 * targets; deeper Sections surface when one of these is focused in turn. Uses
 * the renderer node tree (which includes nested Sections) rather than GQL.
 */
function getDirectChildSectionObjectIds(
  sections: PRMarinaFileSection[],
  focusedSectionUUID: string
): string[] {
  return sections
    .filter(section => {
      const node = section.getNode()
      if (!node || node.getLayerUUID() === focusedSectionUUID) {
        return false
      }

      let ancestor = node.getParentNode()
      while (ancestor && !ancestor.isSection()) {
        ancestor = ancestor.getParentNode()
      }

      return ancestor?.getLayerUUID() === focusedSectionUUID
    })
    .map(section => section.objectId)
}

/**
 * The objectIds of every frame anywhere inside the focused Section (at any
 * depth), so previewing a Section surfaces all of its frames - including ones
 * organised into nested Sections - not only those sitting directly in it.
 */
function getDescendantFrameObjectIds(
  frames: PRMarinaFileArtboard[],
  focusedSectionUUID: string
): string[] {
  return frames
    .filter(frame => {
      for (
        let ancestor = frame.getNode()?.getParentNode();
        ancestor;
        ancestor = ancestor.getParentNode()
      ) {
        if (ancestor.getLayerUUID() === focusedSectionUUID) {
          return true
        }
      }

      return false
    })
    .map(frame => frame.objectId)
}

type PageCanvasFrameOverlayProps = {
  entity: PRMarinaFileArtboard
  isInspectorActive: boolean
  // View mode props
  framesRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  selectedArtboardUUID: string | null
  setSelectedArtboardUUID: (uuid: string | null) => void
  rootExtent: Rect
  isPlacingDraftAnnotation: boolean
  disableInteractions?: boolean
  // In the single-Section focus view, render the overlay even when the entity's
  // layer is hidden (isEnabled() === false), so a focused Section's children
  // stay navigable regardless of their design-time visibility.
  forceShow?: boolean
}

const PageCanvasFrameOverlay = React.memo(function PageCanvasFrameOverlay({
  entity,
  isInspectorActive,
  framesRefs,
  selectedArtboardUUID,
  setSelectedArtboardUUID,
  rootExtent,
  isPlacingDraftAnnotation,
  disableInteractions,
  forceShow,
}: PageCanvasFrameOverlayProps) {
  const node = entity.getNode()

  const isSelected = selectedArtboardUUID === entity.objectId
  const isHidden = node?.isEnabled() === false
  const isSection = node?.isSection()

  // A section's `bounds` field reflects the extent including any overflowing
  // children, which can diverge from the section's own frame as shown in the
  // Mac app. getAbsoluteLayerBounds() reports the section's own frame instead.
  const bounds =
    isSection && node ? node.getAbsoluteLayerBounds() : entity.bounds

  const viewModeProps = useViewModeOverlayProps(
    entity,
    selectedArtboardUUID,
    setSelectedArtboardUUID,
    isPlacingDraftAnnotation,
    !!isSection
  )
  const inspectModeProps = useInspectModeOverlayProps(entity)

  let overlayProps = viewModeProps
  if (disableInteractions) {
    overlayProps = {
      disableAutoHover: true,
    }
  } else if (isInspectorActive) {
    overlayProps = inspectModeProps
  }

  // Hidden entities don't render overlay decorations unless selected - or unless
  // explicitly forced (a focused Section's children must stay overlaid even when
  // their layer is hidden, so they remain navigable).
  if (isHidden && !isSelected && !forceShow) {
    return null
  }

  const isVariantSet = node ? node.getVariantSet().size() > 0 : false

  const isComponent =
    node?.getLayer()?.type.value === PRMarinaLayerType.SymbolMaster ||
    isVariantSet

  const variantPropertyNames = node
    ? convertEmbindVectorToArray(node.getVariantConfiguration()).map(
        ({ valueName }) => valueName
      )
    : undefined

  return (
    <EntityOverlay
      ref={(ref: HTMLDivElement | null) => {
        if (framesRefs.current) {
          framesRefs.current[entity.objectId] = ref
        }
      }}
      bounds={bounds}
      artboardName={entity.name}
      artboardUUID={entity.objectId}
      isComponent={isComponent}
      variantPropertyNames={variantPropertyNames}
      isSection={isSection}
      // A Section with no fill or border of its own is invisible on the canvas -
      // the dashed outline stands in for it, and steps aside as soon as the
      // Section gets a style that paints its own boundary.
      showDashedOutline={isUnstyledSection(node)}
      rootExtent={rootExtent}
      {...overlayProps}
    />
  )
})

/**
 * The overlay we show on top ot the page canvas top level frames. The overlay is used to show
 * the artboards' names, cta, outline and listen for interactions on them.
 * Note: we use a unified component for both view modes to use only one React tree and
 * avoid remounting all overlays when switching modes.
 */
export const PageCanvasFramesOverlays = React.memo(
  function PageCanvasFramesOverlays({
    focusedSectionUUID,
  }: {
    focusedSectionUUID?: string
  }) {
    const { isInspectorActive } = useInspectorContext()
    const topLevelFrames = usePRFileArtboards() ?? []
    const sections = usePRFileSections() ?? []

    // In the single-Section focus view, show overlays for the focused Section's
    // direct child Sections (to drill into) plus every frame it contains at any
    // depth - so previewing a Section surfaces all of its frames, including ones
    // nested in sub-Sections. Computed from the renderer (which, unlike the
    // sections GQL, includes nested entities). On the normal page canvas this is
    // undefined and all entities are shown.
    const childEntityObjectIds = focusedSectionUUID
      ? new Set([
          ...getDirectChildSectionObjectIds(sections, focusedSectionUUID),
          ...getDescendantFrameObjectIds(topLevelFrames, focusedSectionUUID),
        ])
      : undefined

    // Paint larger (outer) sections first so nested (smaller) sections land on
    // top and stay clickable - otherwise a parent section's overlay would cover
    // its children and you couldn't navigate into them from the page canvas.
    const sectionsByContainment = [...sections].sort(
      (a, b) =>
        b.bounds.width * b.bounds.height - a.bounds.width * a.bounds.height
    )

    const allEntities = [...sectionsByContainment, ...topLevelFrames]

    const decoratedEntities = childEntityObjectIds
      ? allEntities.filter(entity => childEntityObjectIds.has(entity.objectId))
      : allEntities

    //@TODO: We should move the selected frame terminology to selected entity/node
    const { framesRefs, selectedArtboardUUID, setSelectedArtboardUUID } =
      useViewModeState()

    const rootExtent = useRootNodeAbsoluteExtent()
    const { isPlacingDraftAnnotation } = useCanvasAnnotations()
    const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

    // View mode global events - only active in view mode
    useViewModeGlobalEvents(
      framesRefs,
      selectedArtboardUUID,
      setSelectedArtboardUUID,
      isInspectorActive
    )

    return (
      <>
        {decoratedEntities.map(entity => (
          <PageCanvasFrameOverlay
            key={entity.objectId}
            entity={entity}
            isInspectorActive={isInspectorActive}
            // View mode props
            framesRefs={framesRefs}
            selectedArtboardUUID={selectedArtboardUUID}
            setSelectedArtboardUUID={setSelectedArtboardUUID}
            rootExtent={rootExtent}
            isPlacingDraftAnnotation={isPlacingDraftAnnotation ?? false}
            disableInteractions={!isCanvasInteractionsEnabled}
            forceShow={!!childEntityObjectIds}
          />
        ))}
      </>
    )
  }
)
